import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Tabs, 
  Tab, 
  List, 
  ListItem, 
  ListItemText, 
  Divider,
  Chip,
  Link,
  Grid,
  Paper
} from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cancelarie-tabpanel-${index}`}
      aria-labelledby={`cancelarie-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Cancelarie: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Cancelarie
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="cancelarie tabs">
          <Tab label="Structura Generală" />
          <Tab label="Cabinetul Episcopului" />
          <Tab label="Contacte" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Vicar Administrativ
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Pr. Gheorghe Militaru"
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Locație: Roma - Ostia
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Link href="mailto:eparhial@dor-italia.it" color="error">
                              eparhial@dor-italia.it
                            </Link>
                          </Box>
                          <Box>
                            <Link href="mailto:vicario.diocesano@dor-italia.it" color="error">
                              vicario.diocesano@dor-italia.it
                            </Link>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Responsabilități
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Administrarea generală a eparhiei"
                      secondary="Coordonarea activităților administrative"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Reprezentarea eparhiei"
                      secondary="În relațiile externe și interne"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Supravegherea activităților"
                      secondary="Monitorizarea implementării deciziilor"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Cabinetul Episcopului
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Secretar Eparhial / Cabinet
                      </Typography>
                      <Typography variant="h6" color="primary">
                        Pr. Daniel Bageac
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Locație: Roma - Laurentina
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Link href="mailto:secretariat@dor-italia.it" color="error">
                          secretariat@dor-italia.it
                        </Link>
                      </Box>
                      <Box>
                        <Link href="mailto:diocesi@dor-italia.it" color="error">
                          diocesi@dor-italia.it
                        </Link>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Secretar Personal Chiriarh
                      </Typography>
                      <Typography variant="h6" color="primary">
                        Protos. Efrem
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Locație: Centrul Eparhial
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Link href="mailto:roma@dor-italia.it" color="error">
                          roma@dor-italia.it
                        </Link>
                      </Box>
                      <Box>
                        <Link href="mailto:amministrativo@dor-italia.it" color="error">
                          amministrativo@dor-italia.it
                        </Link>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Contacte Principale
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Email General"
                      secondary={
                        <Link href="mailto:eparhial@dor-italia.it" color="error">
                          eparhial@dor-italia.it
                        </Link>
                      }
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Secretariat"
                      secondary={
                        <Link href="mailto:secretariat@dor-italia.it" color="error">
                          secretariat@dor-italia.it
                        </Link>
                      }
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Administrativ"
                      secondary={
                        <Link href="mailto:amministrativo@dor-italia.it" color="error">
                          amministrativo@dor-italia.it
                        </Link>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Statut
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="Activ" color="success" />
                  <Chip label="Central" color="primary" />
                  <Chip label="Administrativ" color="secondary" />
                </Box>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Cancelaria este centrul administrativ principal al eparhiei, 
                  responsabil pentru coordonarea tuturor activităților administrative 
                  și de reprezentare.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default Cancelarie;
